/* ----------------- Globals --------------- */
import { Pressable } from "react-native";
import ContentLoader from "react-native-easy-content-loader";
import { Text } from "react-native-paper";

/* ----------------- Types --------------- */
import type { HeaderSectionData } from "@features/dashboard/";

/* ----------------- Hooks --------------- */
import { useAppTheme } from "@hooks/useAppTheme";

/* ----------------- UI --------------- */
import {
	Box,
	DonutChart,
	DonutChartSkeleton,
	TitleSubtitleSkeleton,
	useDevice,
} from "@memorang/ui";

/* ----------------- Components --------------- */
import { SetExamDate } from "components/ExamContextListItem";
import ProductIcon from "components/ProductIcon";

/* ----------------- Icons --------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import type { PracticeTestExam } from "@memorang/types";
import DiagnosticItemsContainer from "containers/DiagnosticItemsContainer";
import type { DiagnosticCardProps } from "types/tag";

interface Props {
	headerData?: HeaderSectionData;
	exams?: PracticeTestExam[];
	hasAccess?: boolean;
	handleShowPurchase: () => void;
	recommendationItems?: DiagnosticCardProps[];
}

const HeaderStatsSkeleton = ({
	isSmallScreen,
	borderColor,
	surfaceColor,
}: {
	isSmallScreen?: boolean;
	borderColor: string;
	surfaceColor: string;
}) => (
	<Box
		flexDirection="row"
		gap={8}
		justifyContent="space-between"
		alignItems="stretch"
		width={isSmallScreen ? "100%" : undefined}
		flex={isSmallScreen ? undefined : 1}
	>
		{[0, 1, 2].map((item) => (
			<Box
				key={item}
				style={{
					flex: 1,
					alignItems: "center",
					justifyContent: "center",
					borderWidth: 1,
					borderColor,
					borderRadius: 0,
					paddingVertical: 12,
					paddingHorizontal: 8,
					gap: 4,
					minWidth: 0,
					backgroundColor: surfaceColor,
				}}
			>
				{/*@ts-ignore*/}
				<ContentLoader
					avatar
					aSize={24}
					aShape="circle"
					title={false}
					pRows={0}
					avatarStyles={{ marginRight: 0 }}
					containerStyles={{ paddingHorizontal: 0, width: 24 }}
				/>
				{/*@ts-ignore*/}
				<ContentLoader
					title={false}
					pRows={2}
					pHeight={[14, 10]}
					pWidth={[24, 44]}
					paragraphStyles={{ alignSelf: "center", marginVertical: 4 }}
					containerStyles={{ paddingHorizontal: 0, alignItems: "center" }}
				/>
			</Box>
		))}
	</Box>
);

const ExamInfoSection = ({
	headerData,
	exams,
	hasAccess,
	handleShowPurchase,
	recommendationItems,
}: Props) => {
	const theme = useAppTheme();
	const { isSmallScreen } = useDevice();
	const statSurfaceColor = theme.colors.surface;
	const sectionBorderColor = theme.colors.outlineVariant || "#eae7de";

	return (
		<Box gap={16}>
			<Box
				style={{
					backgroundColor: theme.colors.surface,
					borderWidth: 1,
					borderColor: sectionBorderColor,
					borderRadius: theme.roundness,
					overflow: "hidden",
				}}
			>
				<Box
					style={{
						backgroundColor: theme.colors.surface,
						padding: 16,
						flexDirection: isSmallScreen ? "column" : "row",
						justifyContent: "space-between",
						alignItems: isSmallScreen ? "stretch" : "center",
						gap: isSmallScreen ? 20 : 24,
					}}
				>
					<Box flexDirection="row" alignItems="center" gap={16} flex={1}>
						{headerData ? (
							<DonutChart
								distribution={headerData.distribution}
								radius={45}
								innerRadius={36}
							>
								{headerData.examIcon ? (
									<ProductIcon
										image={headerData.examIcon}
										size={42}
										showBorder={false}
									/>
								) : (
									<Icon
										name="file-document-outline"
										size={48}
										color={theme.colors.onSurfaceVariant}
									/>
								)}
							</DonutChart>
						) : (
							<DonutChartSkeleton size={90} isSmallScreen={isSmallScreen} />
						)}
						<Box
							flex={1}
							gap={4}
							justifyContent="center"
							style={{ minHeight: 52 }}
						>
							{headerData ? (
								<>
									<Text
										variant="titleLarge"
										style={{
											fontWeight: "bold",
											color: theme.colors.onSurface,
											fontSize: 22,
											lineHeight: 28,
										}}
									>
										{headerData.examName}
									</Text>
									<SetExamDate
										textColor={theme.colors.primary}
										examDate={headerData.examDate}
										toolbarTitle="Select Goal Date"
										okButtonLabel="Set Goal Date"
									/>
								</>
							) : (
								<TitleSubtitleSkeleton />
							)}
						</Box>
					</Box>

					{headerData ? (
						<Box
							flexDirection="row"
							gap={8}
							justifyContent="space-between"
							alignItems="stretch"
							width={isSmallScreen ? "100%" : undefined}
							flex={isSmallScreen ? undefined : 1}
						>
							{headerData.headerStats.map((stat) => (
								<Pressable
									key={`${stat.label}-${stat.value}`}
									onPress={stat.onClick}
									style={{
										flex: 1,
										alignItems: "center",
										borderWidth: 1,
										borderColor: sectionBorderColor,
										borderRadius: 0,
										paddingVertical: 12,
										paddingHorizontal: 8,
										gap: 4,
										minWidth: 0,
										cursor: stat.onClick ? "pointer" : "auto",
										backgroundColor: statSurfaceColor,
									}}
								>
									<Icon
										// biome-ignore lint/suspicious/noExplicitAny: icon name can be generic string
										name={stat.icon as any}
										color={stat.color || theme.colors.onSurfaceVariant}
										size={24}
									/>
									<Text
										variant="titleSmall"
										style={{
											textAlign: "center",
											fontWeight: "bold",
											color: theme.colors.onSurface,
											fontSize: 14,
										}}
									>
										{stat.value}
									</Text>
									<Text
										variant="labelSmall"
										style={{
											color: theme.colors.onSurfaceVariant,
											textAlign: "center",
											fontSize: 10,
										}}
									>
										{stat.label}
									</Text>
								</Pressable>
							))}
						</Box>
					) : (
						<HeaderStatsSkeleton
							isSmallScreen={isSmallScreen}
							borderColor={sectionBorderColor}
							surfaceColor={statSurfaceColor}
						/>
					)}
				</Box>
			</Box>
			<DiagnosticItemsContainer
				exams={exams}
				recommendationItems={recommendationItems}
				hasAccess={hasAccess}
				isDataLoading={!headerData}
				handleShowPurchase={handleShowPurchase}
			/>
		</Box>
	);
};

export default ExamInfoSection;
